import { CSSProperties, ReactNode } from 'react';
import { css } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/person.pb';
import { Icon } from '@frontend/icons';
import { Photos } from '@frontend/photos';
import { breakpoints } from '@frontend/responsiveness';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';

type Contact = Required<Pick<Person, 'firstName' | 'lastName' | 'personId'>> & {
  profileName: string;
  number: string;
  defaultName: string;
};

type ContactActionsProps = {
  callDirection?: string;
  contact: Contact;
  highlightName?: boolean;
  isClickable?: boolean;
  leftElement?: ReactNode;
  locationId: string;
  showAvatar?: boolean;
  showOnlyName?: boolean;
  textStyles?: CSSProperties;
};
export const ContactUserName = ({
  callDirection,
  contact,
  highlightName = true,
  isClickable = false,
  leftElement,
  locationId,
  showAvatar = true,
  showOnlyName = false,
  textStyles,
}: ContactActionsProps) => {
  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore('setShow');

  const { firstName, lastName, profileName, personId, number, defaultName } = contact;
  const isOutboundCall = callDirection === 'outbound';

  return (
    <div css={styles.wrapper}>
      {showAvatar && (
        <Photos.ContactProfilePhoto
          css={styles.avatar(isClickable)}
          disableClick={isClickable || !personId}
          locationId={locationId}
          name={profileName || defaultName}
          personId={personId}
        />
      )}
      <div css={styles.infoStack}>
        <div css={styles.nameWrapper}>
          {firstName || lastName ? (
            <Truncated
              as='div'
              css={styles.name(isClickable)}
              TooltipComponent={
                <Text size='medium' color='white'>
                  {[firstName, lastName].filter(Boolean).join(' ')}
                </Text>
              }
              onClick={() => {
                if (!!personId && isClickable) {
                  setPersonId(personId, undefined, locationId);
                  setShow(true, 'contact');
                }
              }}
            >
              {!!firstName && (
                <Text as='span' weight={highlightName ? 'bold' : 'regular'} style={textStyles}>
                  {firstName}
                </Text>
              )}
              {!!lastName && (
                <Text as='span' style={textStyles}>
                  {' '}
                  {lastName}
                </Text>
              )}
            </Truncated>
          ) : (
            <Text as='span' color='light' style={textStyles}>
              {number}
            </Text>
          )}
          {isOutboundCall && <Icon name='phone-outgoing-small' css={styles.outboundIcon} color='subdued' />}
          {leftElement}
        </div>

        {(firstName || lastName) && !showOnlyName && (
          <Text size='small' color='light' style={textStyles}>
            {number}
          </Text>
        )}
      </div>
    </div>
  );
};

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    width: 100%;
  `,
  avatar: (isClickable: boolean) =>
    css({
      cursor: isClickable ? 'pointer' : 'default',
      flexShrink: 0,
    }),
  infoStack: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
  }),
  name: (isClickable: boolean) =>
    css({
      cursor: isClickable ? 'pointer' : 'default',
      width: 'fit-content',
    }),
  nameWrapper: css`
    display: flex;
    align-items: center;
    width: calc(100% - 16px);
    gap: ${theme.spacing(1)};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      gap: ${theme.spacing(0.5)};
    }
  `,
  outboundIcon: css`
    min-width: ${theme.spacing(2)};
    min-height: ${theme.spacing(2)};
    height: ${theme.spacing(2)};
    width: ${theme.spacing(2)};

    @container (max-width: ${breakpoints.xsmall.max}px) {
      min-width: ${theme.spacing(1.5)};
      min-height: ${theme.spacing(1.5)};
      height: ${theme.spacing(1.5)};
      width: ${theme.spacing(1.5)};
    }
  `,
};
